import { Component, HostBinding, Input } from '@angular/core';
import { MinistryFlags } from 'src/app/core/interfaces/ministryFlags';
import { RndcMinistryEntity } from 'src/app/core/interfaces/rndcMinistry';
import { getRndcMinistryError, RNDC_STATUS_MESSAGES } from 'src/app/modules/rndc/rndc-utils';

/**
 * Sujeto y participio por tipo de entidad. Es el único punto que varía entre las
 * distintas alertas RNDC; si cambia la redacción o se agrega una entidad, se edita aquí.
 * `created` se concuerda en género (la sede → creada).
 */
const RNDC_ALERT_SUBJECTS: { [entityType: string]: { subject: string; created: string } } = {
  thirdParty: { subject: 'El tercero', created: 'creado' },
  thirdPartyAddress: { subject: 'La sede', created: 'creada' },
  trailer: { subject: 'El tráiler', created: 'creado' },
  vehicle: { subject: 'El vehículo', created: 'creado' },
  user: { subject: 'El usuario', created: 'creado' }
};

/**
 * Alerta reutilizable del estado RNDC de una entidad (tercero, sede, tráiler, vehículo, usuario).
 *
 * Lógica (prioridad de arriba hacia abajo):
 * - ministryManualAdjustment true → rojo: requiere un ajuste manual del usuario; bloquea la emisión.
 * - ministryRetry true            → ámbar: la entidad se creó y su emisión aún está pendiente.
 * - ministryRetry false           → verde: la emisión se realizó exitosamente.
 * - todo null/ausente             → creación normal, no se muestra alerta.
 * - ministryContingency definido  → "contingencia"; ausente/null → "intermitencia".
 */
@Component({
  selector: 'app-rndc-contingency-alert',
  templateUrl: './rndc-contingency-alert.component.html',
  styleUrls: ['./rndc-contingency-alert.component.scss']
})
export class RndcContingencyAlertComponent {
  /** Entidad que porta las banderas RNDC (tercero, sede, tráiler, ...). */
  @Input() entity: MinistryFlags;
  /** Tipo de entidad; determina el sujeto del texto. Claves válidas: ver RNDC_ALERT_SUBJECTS. */
  @Input() entityType: string;

  /** Colapsa el host cuando no hay alerta, para no dejar espacios en el layout. */
  @HostBinding('class.d-none') get isHidden(): boolean {
    return !this.show;
  }

  /**
   * 'pending' = ámbar (sin emisión al RNDC), 'success' = verde (emitido exitosamente),
   * null = creación normal (sin alerta).
   */
  get status(): 'pending' | 'success' | 'manual' | null {
    if (!this.entity) return null;
    // Ajuste manual: error que el usuario debe corregir; tiene prioridad y bloquea la retransmisión.
    if (this.entity.ministryManualAdjustment === true) return 'manual';
    if (this.entity.ministryRetry === true) return 'pending';
    if (this.entity.ministryRetry === false) return 'success';
    return null;
  }

  get show(): boolean {
    return this.status !== null && !!RNDC_ALERT_SUBJECTS[this.entityType];
  }

  /** true → alerta verde (éxito). */
  get isSuccess(): boolean {
    return this.status === 'success';
  }

  /** Clase Bootstrap según el estado: verde (éxito), rojo (ajuste manual) o ámbar (pendiente). */
  get alertClass(): string {
    if (this.status === 'success') return 'alert-success';
    if (this.status === 'manual') return 'alert-danger';
    return 'alert-warning';
  }

  /** Ícono según el estado. */
  get iconClass(): string {
    if (this.status === 'success') return 'fa-check-circle';
    if (this.status === 'manual') return 'fa-exclamation-circle';
    return 'fa-exclamation-triangle';
  }

  get message(): string {
    if (!this.show) return '';
    const subj = RNDC_ALERT_SUBJECTS[this.entityType];
    // Modo de creación: contingencia cuando la bandera viene definida (true en ámbar, false en verde);
    // intermitencia cuando no viene (null/undefined).
    const mode = this.entity.ministryContingency != null ? 'contingencia' : 'intermitencia';
    const base = `${subj.subject} fue ${subj.created} en ${mode} del RNDC y`;
    if (this.status === 'manual') {
      // Reutiliza la misma prioridad que las tablas RNDC (reason, errorRNDC, etc.).
      const detail = getRndcMinistryError(this.entity as RndcMinistryEntity);
      if (detail && detail !== RNDC_STATUS_MESSAGES.fallback) {
        return `${base} su emisión requiere un ajuste manual: ${detail}`;
      }
      return `${base} su emisión requiere un ajuste manual.`;
    }
    if (this.status === 'success') {
      return `${base} su emisión se realizó exitosamente.`;
    }
    return `${base} su emisión aún está pendiente.`;
  }
}
